import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LazyQueryExecFunction, QueryResult } from '@apollo/client';
import {
  DataGridPremium as DataGrid,
  DataGridPremiumProps,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { isEqual } from 'date-fns';
import { PageInfo } from 'queries';

import { clearGridState, GridStateKey, restoreGridState, saveGridState } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel, PaginationModel } from 'utils/pagination-model';

import { CustomDataGridFilterPanel } from './custom-data-grid-filter-panel';

type ServerEnabledDataGridProps = {
  queryResult: QueryResult<any, any>;
  executeLazyQuery: LazyQueryExecFunction<any, any>;
  gridStateKey: GridStateKey;
  paginationModel?: PaginationModel;
  baseFilterModelFromDataGridTab?: GridFilterModel;
} & Omit<DataGridPremiumProps, 'rows'>;

export function ServerEnabledDataGrid<T>({
  queryResult,
  executeLazyQuery,
  baseFilterModelFromDataGridTab,
  gridStateKey,
  ...props
}: ServerEnabledDataGridProps): ReactElement {
  const { portalId } = useParams<Record<string, string>>();

  const defaultApiRef = useGridApiRef();
  const apiRef = props.apiRef || defaultApiRef;

  // lazyQuery is used because a regular query would result in unnecessary fetches and renders (screen flash)
  const { data, fetchMore, variables, loading, called: queryHasBeenCalled } = queryResult;

  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: defaultPaginationModel.page,
    pageSize: defaultPaginationModel.pageSize,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    variables.pageFilter
      ? {
          items: variables.pageFilter.map(
            (filter: { column: string; operator: string; value: any }) => ({
              columnField: filter.column,
              operatorValue: filter.operator,
              value: filter.value,
            }),
          ),
        }
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    variables.pageSort ? [variables.pageSort] : undefined,
  );
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: '',
    endCursor: '',
    totalCount: 0,
  });
  const [isInternalUpdate, setIsInternalUpdate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<T[]>([]);

  const lastAppliedVariablesRef = useRef<any>(variables);

  /**
   * Converts the DataGrid's filter model to the PaginationInput's PageFilter[]
   * @param filterModel
   * @returns
   */
  const convertFilterModelToPageFilter = (filterModel: GridFilterModel) => {
    if (!filterModel || filterModel?.items?.length === 0) {
      return [];
    }
    return filterModel?.items
      .filter((item) => item.value != null)
      .map((item) => ({
        column: item.field,
        operator: item.operator,
        value: item.value,
        logicOperator: filterModel.logicOperator,
      }));
  };

  /**
   * Converts the DataGrid's sort model to the PaginationInput's PageSort
   * @param sortModel
   * @returns
   */
  const convertSortModelToPageSort = (sortModel: GridSortModel | undefined) => {
    if (!sortModel || sortModel.length === 0) {
      return undefined;
    }

    const { field, sort } = sortModel[0];

    if (!sort || !field) {
      return undefined;
    }

    return {
      column: field,
      sort: sort,
    };
  };

  /**
   * merges the base filter model from the DataGridTab with the current filter model to support data grid tabs
   */
  const mergedPageFilter = useMemo(() => {
    const combinedFilterModel = {
      items: [...(baseFilterModelFromDataGridTab?.items || []), ...(filterModel?.items || [])],
      logicOperator: filterModel?.logicOperator || baseFilterModelFromDataGridTab?.logicOperator,
    };
    return convertFilterModelToPageFilter(combinedFilterModel);
  }, [baseFilterModelFromDataGridTab, filterModel]);

  // https://mui.com/x/react-data-grid/pagination/#server-side-pagination
  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setIsInternalUpdate(true);
    // Set the initial before or after cursor values
    const currentPage = paginationModel?.page ?? 0;
    const newPage = newPaginationModel?.page ?? 0;
    const edges = data?.data.edges || [];

    if (newPaginationModel.pageSize !== paginationModel.pageSize) {
      setPageInfo({ ...pageInfo, startCursor: '', endCursor: '' });
      setRows([]);
    } else {
      if (newPage === 0) {
        setPageInfo({ ...pageInfo, startCursor: '', endCursor: '' });
      } else if (newPage > currentPage) {
        const lastEdge = edges.length > 0 ? edges[edges.length - 1] : null;
        const after = lastEdge ? lastEdge.cursor : null;
        setPageInfo({ ...pageInfo, startCursor: '', endCursor: after });
      } else if (newPage < currentPage) {
        const firstEdge = edges.length > 0 ? edges[0] : null;
        const before = firstEdge ? firstEdge.cursor : null;
        setPageInfo({ ...pageInfo, startCursor: before, endCursor: '' });
      }
    }
    setPaginationModel(newPaginationModel);
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setIsInternalUpdate(true);
    setFilterModel(newFilterModel);
    handleSaveGridState();
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setIsInternalUpdate(true);
    setSortModel(newSortModel);
    handleSaveGridState();
  };

  useEffect(() => {
    if (!isEqual(variables, lastAppliedVariablesRef.current)) {
      let page: number = defaultPaginationModel.page;
      let pageSize: number = defaultPaginationModel.pageSize;

      if (variables.paginationInput) {
        page = variables.paginationInput.page;
        pageSize = variables.paginationInput.pageSize;
      } else {
        page = lastAppliedVariablesRef.current.input.paginationInput.page;
        pageSize = lastAppliedVariablesRef.current.input.paginationInput.pageSize;
      }

      setPaginationModel({
        page: page,
        pageSize: pageSize,
      });
      setPageInfo(
        variables.pageInfo || {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: '',
          endCursor: '',
          totalCount: 0,
        },
      );
      lastAppliedVariablesRef.current = variables;
      setIsInternalUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables]);

  useEffect(
    () => {
      // this prevents a duplicate fetch operation from the refetch operation on the parent component
      if (!isInternalUpdate) {
        return;
      }

      const newVariables: any = {
        input: {
          ...lastAppliedVariablesRef.current.input,
          paginationInput: {
            page: paginationModel?.page || 0,
            pageSize: paginationModel?.pageSize || 100,
            pageFilter: mergedPageFilter,
            pageSort: convertSortModelToPageSort(sortModel),
            after: pageInfo.endCursor || '',
            before: pageInfo.startCursor || '',
          },
        },
      };

      if (isEqual(newVariables, lastAppliedVariablesRef.current)) {
        return;
      }

      const fetchMoreResults = async () => {
        try {
          setIsLoading(true);
          const results = await fetchMore({
            variables: newVariables,
            updateQuery: (prev, { fetchMoreResult }) => {
              //if no new data, return previous data (this will result in a new query, duplicates in the log)
              if (!fetchMoreResult) {
                return prev;
              }
              return { ...fetchMoreResult };
            },
          });
          if (results.errors && results.errors.length > 0) {
            throw new Error(results.errors.map((err) => err.message).join(', '));
          }
        } catch (e) {
          // this is required to prevent filters from auto reloading in a broken state forcing the user to clear application storage data
          clearGridState(gridStateKey);
          setRows([]);
          showAlert({
            message: (e as Error).message,
            severity: NotificationSeverity.ERROR,
          });
        } finally {
          setIsLoading(false);
        }
      };

      if (queryHasBeenCalled) {
        fetchMoreResults();
      }

      lastAppliedVariablesRef.current = newVariables;
      setIsInternalUpdate(false);
      handleSaveGridState();
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterModel, sortModel, paginationModel, pageInfo, fetchMore, variables],
  );

  useEffect(() => {
    const newData = data?.data;

    if (!newData) {
      setRows([]);
      return;
    }

    let newRows: any = [];
    newRows = [
      ...(newData.edges ?? [])
        .map((edge: any) => edge.node)
        .filter((node: any): node is T => !!node),
    ];

    setIsLoading(false);
    setRows(newRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.edges]);

  useEffect(() => {
    restoreGridState(apiRef.current, gridStateKey, portalId);

    if (!queryHasBeenCalled) {
      // save the state on initial load to facilitate refreshGridState from other components
      saveGridState(apiRef.current, gridStateKey, portalId);
      executeLazyQuery();
    }

    // a listener to refresh the grid state from other components (essentially a refetch)
    const handleRefreshGridState = (event: Event) => {
      const customEvent = event as CustomEvent<{ gridStateKey: GridStateKey }>;
      if (customEvent.detail?.gridStateKey === gridStateKey) {
        restoreGridState(apiRef.current, gridStateKey, portalId);
      }
    };

    window.addEventListener('refreshGridState', handleRefreshGridState);

    // clean-up
    return () => {
      window.removeEventListener('refreshGridState', handleRefreshGridState);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, gridStateKey]);

  const handleSaveGridState = () => {
    saveGridState(apiRef.current, gridStateKey, portalId);
  };

  return (
    <div>
      <DataGrid
        apiRef={apiRef}
        {...props}
        loading={loading || isLoading}
        rows={rows}
        rowCount={data?.data?.pageInfo?.totalCount || 0}
        pagination
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        onPaginationModelChange={handlePaginationModelChange}
        paginationModel={paginationModel}
        onFilterModelChange={handleFilterModelChange}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        sortModel={sortModel}
        slots={{
          ...props.slots,
          filterPanel: CustomDataGridFilterPanel,
        }}
        onColumnVisibilityModelChange={() => {
          handleSaveGridState();
        }}
        onColumnWidthChange={() => {
          handleSaveGridState();
        }}
      />
    </div>
  );
}
